"use client";

import { useCallback, useRef, useState, useSyncExternalStore } from "react";
import Image from "next/image";
import NextLink from "next/link";

import { track } from "@/lib/analytics/data-layer";
import { cn } from "@/lib/utils";
import { IMAGE_QUALITY, IMAGE_SIZES_PRESETS } from "@/lib/images/image-config";

/** Which side of the bifurcation is revealed; `idle` is the neutral copy. */
type HeroState = "idle" | "gift" | "list";

interface HeroLayer {
  eyebrow: string;
  title: string;
  subtitle: string;
  /** The wide crop, from a tablet up. */
  img: string;
  /** The tall crop, on a phone: the designer framed each subject twice. */
  imgMobile: string;
}

/**
 * Copy and photography from the reference the company approved on 2026-09-21
 * (`docs/internal/Homepage Lista.html`, images in `docs/internal/JPEGS/`).
 *
 * Each layer carries the photograph in two crops: a wide one and a tall one, because the
 * designer framed each subject twice rather than letting one frame be cropped to both shapes.
 */
const HERO_LAYERS: Record<HeroState, HeroLayer> = {
  idle: {
    eyebrow: "Lista de Casamento",
    title: "Uma experiência personalizada para celebrar um novo capítulo",
    subtitle: "Crie a sua lista com acolhimento, curadoria e atendimento próximo em cada etapa.",
    img: "/images/homepage/hero-abertura.jpg",
    imgMobile: "/images/homepage/hero-abertura-mobile.jpg",
  },
  gift: {
    eyebrow: "Para convidados",
    title: "Um presente à altura da celebração",
    subtitle:
      "Encontre peças especiais para celebrar os noivos e fazer parte de um dos momentos mais importantes de sua história.",
    img: "/images/homepage/presentear.jpg",
    imgMobile: "/images/homepage/presentear-mobile.jpg",
  },
  list: {
    eyebrow: "Para noivos",
    title: "Uma lista feita para vocês",
    subtitle:
      "Pensada para tornar cada escolha memorável, com curadoria, acolhimento e atendimento próximo em todos os momentos da sua jornada.",
    img: "/images/homepage/crie-sua-lista.jpg",
    imgMobile: "/images/homepage/crie-sua-lista-mobile.jpg",
  },
};

/** Render order of the stacked layers. */
const HERO_STATES: HeroState[] = ["idle", "gift", "list"];

// ─── Pointer capability ──────────────────────────────────────────────────────

const HOVER_QUERY = "(hover: hover) and (pointer: fine)";

function subscribeHover(onChange: () => void) {
  const mediaQuery = window.matchMedia(HOVER_QUERY);
  mediaQuery.addEventListener("change", onChange);
  return () => mediaQuery.removeEventListener("change", onChange);
}

/**
 * Whether the device has a real hover-capable pointer, gating the layer swap —
 * on touch, hover fires on tap and shifts the card out from under the finger
 * (Tech Spec § Breakpoints). Read through `useSyncExternalStore`, not an effect,
 * to track the query and keep the server snapshot matching the first paint.
 */
function useCanHover(): boolean {
  return useSyncExternalStore(
    subscribeHover,
    () => window.matchMedia(HOVER_QUERY).matches,
    () => false,
  );
}

/** Ties each hovered side to the CTA it belongs to, for analytics. */
const JOURNEY_BY_STATE = {
  gift: { journey: "guest", itemId: "guest_find_list", href: "/listas" },
  list: { journey: "host", itemId: "host_create_list", href: "/onboarding" },
} as const satisfies Record<Exclude<HeroState, "idle">, unknown>;

// ─── Door card (canon MOL-03) ────────────────────────────────────────────────

/** The reference's gift box, stroke 1.5 at 24px (canon ATM-12). */
function GiftIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="8" width="18" height="4" rx="1" />
      <path d="M5 12v9h14v-9M12 8v13" />
      <path d="M12 8S9.6 8 8.6 7A2 2 0 0 1 12 5a2 2 0 0 1 3.4 2c-1 1-3.4 1-3.4 1z" />
    </svg>
  );
}

/** The reference's list with a plus, stroke 1.5 at 24px. */
function ListPlusIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 10V5.5a1.5 1.5 0 0 0-1.5-1.5h-11A1.5 1.5 0 0 0 5 5.5v13A1.5 1.5 0 0 0 6.5 20H12" />
      <path d="M8.5 8.5h7" />
      <path d="M8.5 12h5" />
      <path d="M8.5 15.5h3" />
      <path d="M18 14v6M15 17h6" />
    </svg>
  );
}

interface DoorCardProps {
  side: Exclude<HeroState, "idle">;
  /** The hero shows this card's side: green rule, filled circle (the reference's `cardStyle`). */
  active: boolean;
  eyebrow: string;
  title: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onBlur: () => void;
  onClick: () => void;
}

/**
 * One of the hero's two doors. The guest's card carries its icon on the right; the host's
 * mirrors it — icon on the left, words flush right (canon MOL-03).
 */
function DoorCard({ side, active, eyebrow, title, onMouseEnter, onMouseLeave, onFocus, onBlur, onClick }: DoorCardProps) {
  const mirrored = side === "list";
  const icon = (
    <span
      className={cn(
        "grid size-[44px] flex-none place-items-center rounded-full transition-[background-color,color] duration-500 min-[900px]:size-[46px]",
        active ? "bg-tb-green text-[var(--tb-porcelain-tint)]" : "bg-[rgba(61,63,57,0.08)] text-[var(--fg)]",
      )}
    >
      {mirrored ? <ListPlusIcon /> : <GiftIcon />}
    </span>
  );

  return (
    <NextLink
      href={JOURNEY_BY_STATE[side].href}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      onClick={onClick}
      className={cn(
        "pointer-events-auto flex w-full items-center gap-[14px] rounded-[4px] border bg-[var(--tb-porcelain-tint)] p-[16px_18px] shadow-[0_18px_44px_rgba(24,20,14,0.28)] transition-[translate,box-shadow,border-color] duration-[450ms] ease-[var(--tb-ease-linen)] hover:-translate-y-2 hover:shadow-[0_28px_62px_rgba(24,20,14,0.38)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2 min-[768px]:w-[min(380px,42vw)] min-[900px]:gap-[18px] min-[900px]:p-[20px_22px]",
        active ? "border-[rgba(102,145,86,0.55)]" : "border-[rgba(61,63,57,0.12)]",
      )}
    >
      {mirrored ? icon : null}
      <div className={cn("min-w-0 flex-1", mirrored && "text-right")}>
        <div className="text-[12px] uppercase tracking-[0.18em] text-[var(--fg-muted)]">{eyebrow}</div>
        <div className="mt-[7px] font-display text-[17px] uppercase leading-[1.15] text-[var(--fg)] min-[900px]:text-[clamp(16px,1.45vw,20px)]">
          {title}
        </div>
      </div>
      {mirrored ? null : icon}
    </NextLink>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

interface HeroSectionProps {
  /** Parallax target, driven by `--heroPY` from `useScrollEffects`. */
  heroStageRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Hero — splits the visitor into the guest and host journeys before asking for
 * anything (Tech Spec § US-01/US-02).
 *
 * Three stacked layers cross-fade by opacity; hovering a CTA card previews its
 * side and leaving restores `idle`.
 */
export function HeroSection({ heroStageRef }: HeroSectionProps) {
  const [state, setState] = useState<HeroState>("idle");
  const canHover = useCanHover();

  // Hover fires continuously: one report per side per page view.
  const reportedPreviews = useRef(new Set<HeroState>());

  // Shared by hover and focus, so the once-per-side budget covers both.
  const previewSide = useCallback((next: HeroState) => {
    setState(next);

    if (next === "idle" || reportedPreviews.current.has(next)) return;
    reportedPreviews.current.add(next);
    const { journey, itemId } = JOURNEY_BY_STATE[next];
    track({
      event: "select_content",
      content_type: "hero_journey",
      item_id: itemId,
      location: "hero",
      journey,
    });
  }, []);

  const handleCardEnter = useCallback((next: HeroState) => () => {
    if (canHover) previewSide(next);
  }, [canHover, previewSide]);

  const handleCardLeave = useCallback(() => {
    if (canHover) setState("idle");
  }, [canHover]);

  // Focus is not gated by pointer capability: reaching the preview with a
  // keyboard must not depend on owning a mouse.
  const handleCardFocus = useCallback(
    (next: HeroState) => () => previewSide(next),
    [previewSide],
  );

  const handleCardBlur = useCallback(() => setState("idle"), []);

  const trackJourneyCta = useCallback((next: Exclude<HeroState, "idle">) => {
    const { journey, itemId, href } = JOURNEY_BY_STATE[next];
    track({
      event: "select_content",
      content_type: "cta",
      item_id: itemId,
      location: "hero",
      journey,
      link_url: href,
    });
  }, []);

  return (
    <section id="top" className="relative min-h-[100svh] overflow-hidden">
      {/*
        All three backgrounds are mounted and cross-faded by opacity. Swapping a
        single `src` on hover would flash while the new file downloads, so the
        trade is upfront bytes for an instant swap. Only the idle layer is
        `priority` — it is the LCP candidate, the other two are lazy.
      */}
      <div
        ref={heroStageRef}
        className="absolute inset-0 z-0 will-change-transform motion-reduce:!transform-none"
        style={{ transform: "translate3d(0, var(--heroPY, 0px), 0)" }}
      >
        {HERO_STATES.map((layerState) =>
          /*
           * Two crops of the same photograph, one shown at a time (`md:hidden` / `hidden md:block`):
           * the reference ships a wide frame and a tall one per layer, so a phone gets the subject
           * the designer framed for it instead of the middle of a landscape.
           */
          (["mobile", "wide"] as const).map((crop) => (
            <Image
              key={`${layerState}-${crop}`}
              src={crop === "mobile" ? HERO_LAYERS[layerState].imgMobile : HERO_LAYERS[layerState].img}
              // Decorative: the copy layer carries the message, and two of the three
              // are invisible at any moment.
              alt=""
              fill
              sizes={IMAGE_SIZES_PRESETS.fullBleed}
              quality={IMAGE_QUALITY.high}
              preload={layerState === "idle"}
              // `preload` only inserts the link; the image itself also asks for the high fetch priority
              // and the eager load, as the Next 16 guide recommends for the LCP image (PERF-06).
              {...(layerState === "idle" ? { loading: "eager" as const, fetchPriority: "high" as const } : {})}
              className={cn(
                "object-cover transition-opacity duration-[900ms] ease-linear scale-[1.40]",
                crop === "mobile" ? "[@media(min-width:768px)]:hidden" : "hidden [@media(min-width:768px)]:block",
              )}
              style={{
                objectPosition: layerState === "idle" ? "center 30%" : "center",
                opacity: state === layerState ? 1 : 0,
              }}
            />
          )),
        )}
      </div>

      {/* Oxide wash that keeps the white copy readable over any of the three
          photos (Tech Spec § Acessibilidade). */}
      <div
        className="absolute inset-0 z-[1]"
        style={{
          background: "radial-gradient(115% 80% at 50% 46%, rgba(24,20,14,0.5) 0%, rgba(24,20,14,0.16) 46%, rgba(24,20,14,0) 74%), linear-gradient(180deg, rgba(24,20,14,0.7) 0%, rgba(24,20,14,0.18) 30%, rgba(24,20,14,0.2) 60%, rgba(24,20,14,0.8) 100%)",
          // The reference's own scrim strength (`heroOverlay`, 0.85; canon ORG-03).
          opacity: 0.85,
        }}
      />

      {/* Copy layers — same cross-fade as the backgrounds, one per state.
          All three stay mounted so the cross-fade never waits on a paint, but
          only the idle layer's title is an `h1`: the other two carry different
          copy, and three competing level-1 headings in the markup dilute the
          page's topic for crawlers. The rest are styled identically. */}
      {HERO_STATES.map((layerState) => {
        const layer = HERO_LAYERS[layerState];
        const isActive = state === layerState;
        const TitleTag = layerState === "idle" ? "h1" : "p";
        return (
          <div
            key={layerState}
            className="absolute inset-0 z-[2] flex flex-col items-center justify-center px-[clamp(20px,5vw,40px)] text-center transition-opacity duration-[600ms] ease-linear"
            style={{
              // Clears the fixed header, then the CTA cards pinned to the bottom.
              paddingTop: "96px",
              paddingBottom: "clamp(150px, 22svh, 240px)",
              opacity: isActive ? 1 : 0,
              // Faded-out layers stay in the DOM, so their links and text must
              // not be clickable or hoverable.
              pointerEvents: isActive ? "auto" : "none",
            }}
          >
            {/* The canon's eyebrow over a photograph (ATM-01): 15px, 500, white. */}
            <span className="mb-5 text-[15px] font-medium uppercase tracking-[0.16em] text-white">
              {layer.eyebrow}
            </span>
            {/* Canon ATM-02, hero scale: uppercase is the component's, the copy stays in sentence
                case. The resting title is allowed the reference's wider measure (1080px). */}
            <TitleTag
              className={cn(
                "mx-auto font-display text-[clamp(28px,4.37vw,62px)] font-normal uppercase leading-[1.05] tracking-[-0.005em] text-white [text-shadow:0_2px_40px_rgba(0,0,0,0.25)] [text-wrap:balance]",
                layerState === "idle" ? "max-w-[1080px]" : "max-w-[760px]",
              )}
            >
              {layer.title}
            </TitleTag>
            <p className="mx-auto mt-[22px] max-w-[460px] text-[clamp(15px,1.1vw,17px)] leading-[1.6] text-white [text-wrap:pretty]">
              {layer.subtitle}
            </p>
          </div>
        );
      })}

      {/* The bifurcation: guest left, host right. Hovering a card reveals the
          matching layer; the cards are plain links, so the journeys work
          without JS and on touch. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-[clamp(24px,4vh,48px)] z-[3] flex flex-col gap-[clamp(12px,2vw,18px)] px-[clamp(16px,4vw,48px)] [@media(min-width:768px)]:flex-row [@media(min-width:768px)]:justify-between">
        <DoorCard
          side="gift"
          active={state === "gift"}
          eyebrow="Para convidados"
          title="Comprar um presente"
          onMouseEnter={handleCardEnter("gift")}
          onMouseLeave={handleCardLeave}
          onFocus={handleCardFocus("gift")}
          onBlur={handleCardBlur}
          onClick={() => trackJourneyCta("gift")}
        />

        <DoorCard
          side="list"
          active={state === "list"}
          eyebrow="Para noivos"
          title="Crie ou acesse sua lista"
          onMouseEnter={handleCardEnter("list")}
          onMouseLeave={handleCardLeave}
          onFocus={handleCardFocus("list")}
          onBlur={handleCardBlur}
          onClick={() => trackJourneyCta("list")}
        />
      </div>
    </section>
  );
}
